import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import * as superAdminService from '../services/superAdminService';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    websiteUrl: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || user.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [adminsData, statsData] = await Promise.all([
        superAdminService.getAllAdmins(),
        superAdminService.getAdminStats()
      ]);
      setAdmins(adminsData.admins);
      setStats(statsData.stats);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await superAdminService.createAdmin(formData);
      setSuccess('Admin created successfully!');
      setShowCreateModal(false);
      setFormData({ username: '', email: '', password: '', websiteUrl: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create admin');
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await superAdminService.updateAdmin(selectedAdmin._id, {
        username: formData.username,
        email: formData.email,
        websiteUrl: formData.websiteUrl,
        isActive: formData.isActive
      });
      setSuccess('Admin updated successfully!');
      setShowEditModal(false);
      setSelectedAdmin(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update admin');
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;

    try {
      await superAdminService.deleteAdmin(id);
      setSuccess('Admin deleted successfully!');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await superAdminService.resetAdminPassword(selectedAdmin._id, newPassword);
      setSuccess('Password reset successfully!');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedAdmin(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      username: admin.username,
      email: admin.email,
      websiteUrl: admin.websiteUrl || '',
      isActive: admin.isActive
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (admin) => {
    setSelectedAdmin(admin);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  if (loading) {
    return (
      <>
        <Sidebar />
        <div className="dashboard">
          <Navbar />
          <div className="container">
            <div className="loading">Loading...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="dashboard">
        <Navbar />
        <div className="container">
          <div className="superadmin-dashboard">
            <div className="dashboard-header">
              <h1>Super Admin Dashboard</h1>
              <button 
                className="btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                + Create Admin
              </button>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* Statistics Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <h3>{stats.totalAdmins || 0}</h3>
                  <p>Total Admins</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <h3>{stats.activeAdmins || 0}</h3>
                  <p>Active Admins</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">❌</div>
                <div className="stat-info">
                  <h3>{stats.inactiveAdmins || 0}</h3>
                  <p>Inactive Admins</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👤</div>
                <div className="stat-info">
                  <h3>{stats.totalUsers || 0}</h3>
                  <p>Total Users</p>
                </div>
              </div>
            </div>

            {/* Admins Table */}
            <div className="admins-section">
              <h2>Admin Accounts</h2>
              <div className="table-container">
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Website URL</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin) => (
                      <tr key={admin._id}>
                        <td>{admin.username}</td>
                        <td>{admin.email}</td>
                        <td>
                          {admin.websiteUrl ? (
                            <a href={admin.websiteUrl} target="_blank" rel="noopener noreferrer">
                              {admin.websiteUrl}
                            </a>
                          ) : (
                            <span className="text-muted">Not set</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                            {admin.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => openEditModal(admin)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-icon btn-password"
                              onClick={() => openPasswordModal(admin)}
                              title="Reset Password"
                            >
                              🔑
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDeleteAdmin(admin._id)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {admins.length === 0 && (
                  <div className="no-data">No admins found. Create your first admin!</div>
                )}
              </div>
            </div>

            {/* Create Admin Modal */}
            {showCreateModal && (
              <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Create New Admin</h2>
                    <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
                  </div>
                  <form onSubmit={handleCreateAdmin}>
                    <div className="form-group">
                      <label>Username *</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                        minLength="3"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Password *</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength="6"
                      />
                    </div>
                    <div className="form-group">
                      <label>Website URL (Optional)</label>
                      <input
                        type="url"
                        value={formData.websiteUrl}
                        onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                        placeholder="https://example.com"
                      />
                      <small>The website this admin will manage</small>
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        Create Admin
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Admin Modal */}
            {showEditModal && (
              <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Edit Admin</h2>
                    <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
                  </div>
                  <form onSubmit={handleUpdateAdmin}>
                    <div className="form-group">
                      <label>Username</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                        minLength="3"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Website URL</label>
                      <input
                        type="url"
                        value={formData.websiteUrl}
                        onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                        placeholder="https://example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        Update Admin
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Reset Password Modal */}
            {showPasswordModal && (
              <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Reset Password</h2>
                    <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
                  </div>
                  <form onSubmit={handleResetPassword}>
                    <div className="form-group">
                      <label>Admin: {selectedAdmin?.username}</label>
                    </div>
                    <div className="form-group">
                      <label>New Password *</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength="6"
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        Reset Password
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminDashboard;
